/**
 * Handles the email verification link silently.
 * Calls backend /auth/confirm and redirects to /login with query params
 * used by LoginPage to show toast messages.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { confirmApi } from '@/api/auth.api'
import { toast } from '@/components/ui/toaster/toasterHelpers'

export default function AuthCallbackHandler() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // 👇 prevents double execution in StrictMode
  const didRun = useRef(false)

  const verify = useCallback(async () => {
    if (didRun.current) return
    didRun.current = true

    const token = searchParams.get('token')

    if (!token) {
      navigate('/login?verified=0&reason=invalid')
      return
    }

    try {
      await confirmApi(token)
      toast.success('Konto erfolgreich bestätigt', 'Sie können sich jetzt anmelden.', {
        duration: 8000,
      })
    } catch (err: any) {
      console.error('Email verification failed:', err)

      const code = err?.code?.toUpperCase?.() || ''
      const message = (err?.message || '').toLowerCase()

      if (
        code === 'CONFIRMATION_TOKEN_EXPIRED' ||
        message.includes('confirmation link') ||
        message.includes('request a new')
      ) {
        toast.error(
          'Der Bestätigungslink ist abgelaufen',
          'Bitte fordern Sie einen neuen Link an.',
          { duration: 8000 }
        )
      } else if (code === 'INVALID_PAYLOAD' || message.includes('please check your input')) {
        toast.error(
          'Der Bestätigungslink ist ungültig oder wurde bereits verwendet.',
          'Bitte fordern Sie einen neuen Link an.',
          { duration: 8000 }
        )
      } else {
        toast.error('Bestätigung fehlgeschlagen.', 'Bitte fordern Sie einen neuen Link an.', {
          duration: 8000,
        })
      }
    } finally {
      navigate('/login')
    }
  }, [navigate, searchParams])

  useEffect(() => {
    verify()
  }, [verify])

  return null
}
